/*
  NUDIENT: Paketens delar, paket D1 del f, M-084 svaren efter kontrollen och
  svaren på planen punkt 17.6. Plan: nudient/PLAN-D1-kopkolumn.md punkt 17.6
  och 17.7. Laddas av blocks/nudient-includes.liquid och
  blocks/nudient-set-selector.liquid. En modul körs bara en gång, också när
  båda blocken laddar den.

  Liquid kan inte läsa ett pakets delar, planen punkt 17.4. Skriptet frågar
  Shopifys öppna gränssnitt, Storefront API, utan nyckel, med marknaden och
  språket ur sidan. Samma fråga ger delarna till innehållslistan och
  paketens färg till setväljaren, M-084 fråga 6.

  - Innehållslistan, Info/Includes 417:1359: raderna fylls i med delens
    bild, namn, storlek, färg och pris. Platsen för två rader är reserverad i
    Liquid, det vanligaste antalet, M-084. Ett paket med tre delar får en
    rad till.
  - Setväljaren, Info/SetSelector 416:1383: korten för paketen i samma färg
    som sidans paket visas. Platsen för tre kort är reserverad i Liquid.
  - Svaret sparas i sessionStorage per paket, marknad och språk, så att
    bakåt och framåt inte frågar igen.
  - Svarar gränssnittet inte inom 3 sekunder, eller svarar fel, döljs båda
    blocken. Köpknappen påverkas aldrig, M-084.
  - Litern, "CABIN · 40 L" i 417:1285, M-096 punkt 3 och tillägget punkt
    1: gränssnittet utan nyckel lämnar inte ut produktfält. Skriptet frågar
    därför temat självt om varje dels travel.capacity, med Section Rendering
    API och sections/nudient-spec-data.liquid, en förfrågan per del, när
    raderna redan är ifyllda. Svaret sparas i sessionStorage per del. Utan
    svar, eller utan fält, står bara storleken kvar.
  - Delarna står i prisordning, billigast först. Det ger ritningens ordning,
    Cabin, Check-in (M) och Check-in (L), ur butikens priser, utan att
    gissa ur namnen. Svaret från gränssnittet har ingen fast ordning.
  - Priserna formateras med Horizons formatMoney, som gör samma sak som
    Liquids prisfilter, med butikens prisformat ur sidan. Ören visas bara
    när de inte är noll, som money_without_trailing_zeros och M-057.

  UNDERHÅLL: API_VERSION ska bytas till senaste stabila version ungefär en
  gång om året. Shopify stöder varje version i minst tolv månader. Se
  STATUS.md, "Underhåll efter lansering".

  Värden som inte är tokens:
  - 2026-10: senaste stabila versionen av Storefront API, 7 oktober 2026.
  - 3000: väntetiden i millisekunder, M-084.
  - 10: högst tio delar per paket i frågan. Butiken har högst tre.
  - 112 och 140: bilden i dubbel upplösning, 56 × 70 i 417:1282.
  - Color och Size: optionernas plats, färg först och storlek som andra, M-175.
    size och en: kontrollen på engelska, som skriver en varning i konsolen.
  - nudient-bundle:5: början på nyckeln, 5 sedan M-175, i sessionStorage. Siffran byts när
    det sparade svaret ändrar form.
  - nudient-capacity:1: nyckeln för en dels liter i sessionStorage.
  - nudient-spec-data: sektionen och svarets id.
  - [size] och [capacity]: platshållarna i texten för storlek med liter.
  - 1: en hel valutaenhet, för att få valutans antal decimaler ur Horizon.
*/

import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const API_VERSION = '2026-10';
const TIMEOUT = 3000;

/* Setväljaren skrivs bara ut när sidans produkt står i dess lista, och
   innehållslistan används bara då, M-054 och tillägget till M-084 8 oktober
   2026. Annars görs ingen förfrågan. */
const selector = document.querySelector('[data-nudient-set-selector]');
const includes = selector ? document.querySelector('[data-nudient-bundle-includes]') : null;
const source = includes || selector;

if (source) load();

async function load() {
  const { handle, country, language, moneyFormat } = source.dataset;
  const listHandles = selector ? [...selector.querySelectorAll('[data-nudient-set-handle]')].map((el) => el.dataset.nudientSetHandle) : [];
  const handles = [...new Set([handle, ...listHandles])];

  let bundles;
  try {
    bundles = await fetchBundles(handles, country, language);
  } catch {
    hide();
    return;
  }

  const own = bundles[handle];
  if (!own || own.parts.length === 0) {
    hide();
    return;
  }

  if (includes) fillIncludes(own, moneyFormat);
  if (selector) fillSelector(bundles, own);
}

function hide() {
  for (const block of [includes, selector]) block?.setAttribute('hidden', '');
}

async function fetchBundles(handles, country, language) {
  const key = `nudient-bundle:5:${API_VERSION}:${country}:${language}:${handles.join(',')}`;
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch {}

  const fields = handles
    .map(
      (h, i) => `b${i}: product(handle: ${JSON.stringify(h)}) {
        handle
        variants(first: 1) { nodes { components(first: 10) { nodes {
          quantity
          productVariant {
            price { amount currencyCode }
            selectedOptions { name value }
            image { url(transform: { maxWidth: 112, maxHeight: 140, crop: CENTER }) }
            product { title handle }
          }
        } } } }
      }`
    )
    .join('\n');
  const context = `@inContext(country: ${country}, language: ${language.toUpperCase().replace('-', '_')})`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT);
  const response = await fetch(`${location.origin}/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: `query ${context} { ${fields} }` }),
    signal: controller.signal,
  });
  clearTimeout(timer);
  const json = await response.json();
  if (!response.ok || json.errors) throw new Error('Storefront API');

  const bundles = {};
  for (const product of Object.values(json.data || {})) {
    if (!product) continue;
    const parts = (product.variants.nodes[0]?.components.nodes || []).map((node) => {
      /* Färgen är alternativ 1 och storleken alternativ 2, M-175: namnen är
         översatta på kundens språk. Paketens delar är resväskor med Color,
         Size. En annan ordning skrivs i konsolen, för kontrollen. */
      const options = node.productVariant.selectedOptions;
      if (options.length !== 2 || (language === 'en' && options[1].name.toLowerCase() !== 'size')) {
        console.warn('NUDIENT: paketdelen har inte alternativen Color, Size', node.productVariant.product.handle);
      }
      const option = (name) => (name === 'size' ? options[1] : options[0])?.value || '';
      return {
        quantity: node.quantity,
        title: node.productVariant.product.title,
        handle: node.productVariant.product.handle,
        size: option('size'),
        colour: option('color'),
        amount: node.productVariant.price.amount,
        currency: node.productVariant.price.currencyCode,
        image: node.productVariant.image?.url || '',
      };
    });
    /* Billigast först, som ritningen: Cabin, Check-in (M), Check-in (L) */
    parts.sort((a, b) => Number(a.amount) - Number(b.amount));
    const colours = [...new Set(parts.map((part) => part.colour.toLowerCase()))];
    bundles[product.handle] = { parts, colour: colours.length === 1 ? colours[0] : '' };
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(bundles));
  } catch {}
  return bundles;
}

function money(amount, currency, format) {
  const unit = convertMoneyToMinorUnits('1', currency) || 1;
  const minor = Math.round(Number(amount) * unit);
  const formatted = formatMoney(minor, format, currency);
  if (unit === 1 || minor % unit !== 0) return formatted;

  /* Hela belopp utan ören, som money_without_trailing_zeros: decimaltecknet
     och nollorna efter det sista talet tas bort, oavsett prisformat */
  const zeros = String(unit).length - 1;
  return formatted.replace(new RegExp(`[.,]0{${zeros}}(?=\\D*$)`), '');
}

function fillIncludes(own, moneyFormat) {
  const list = includes.querySelector('[data-nudient-includes-list]');
  const template = includes.querySelector('template[data-nudient-includes-row]');
  const rows = [...list.children];

  own.parts.forEach((part, index) => {
    let row = rows[index];
    if (!row) {
      row = template.content.firstElementChild.cloneNode(true);
      list.append(row);
    }
    const image = row.querySelector('[data-image]');
    if (part.image) {
      const img = document.createElement('img');
      img.src = part.image;
      img.alt = '';
      img.width = 56;
      img.height = 70;
      img.loading = 'lazy';
      image.replaceChildren(img);
    }
    row.querySelector('[data-name]').textContent = part.quantity > 1 ? `${part.quantity} × ${part.title}` : part.title;
    row.querySelector('[data-size]').textContent = part.size;
    row.querySelector('[data-colour]').textContent = part.colour ? `${includes.dataset.colourLabel} · ${part.colour}` : '';
    row.querySelector('[data-price]').textContent = money(part.amount, part.currency, moneyFormat);
    row.removeAttribute('data-placeholder');
  });
  for (const extra of rows.slice(own.parts.length)) extra.remove();

  const count = own.parts.reduce((sum, part) => sum + part.quantity, 0);
  const label = count === 1 ? includes.dataset.countOne : includes.dataset.countOther;
  includes.querySelector('[data-count]').textContent = label.replace('[count]', count);

  fillCapacities(own.parts, [...list.children]);
}

/** Delens liter ur temat, sections/nudient-spec-data.liquid */
async function capacity(handle) {
  const key = `nudient-capacity:1:${handle}`;
  try {
    const saved = sessionStorage.getItem(key);
    if (saved !== null) return JSON.parse(saved);
  } catch {}

  const root = window.Shopify?.routes?.root || '/';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT);
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=nudient-spec-data`, { signal: controller.signal });
  clearTimeout(timer);
  if (!response.ok) return null;
  const script = new DOMParser().parseFromString(await response.text(), 'text/html').getElementById('nudient-spec-data');
  const value = JSON.parse(script?.textContent || 'null')?.capacity ?? null;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
  return value;
}

function fillCapacities(parts, rows) {
  const template = includes.dataset.sizeCapacity;
  if (!template) return;
  parts.forEach(async (part, index) => {
    if (!part.handle || !part.size) return;
    let litres = null;
    try {
      litres = await capacity(part.handle);
    } catch {
      return;
    }
    if (litres === null || litres === '') return;
    const size = rows[index]?.querySelector('[data-size]');
    if (size) size.textContent = template.replace('[size]', part.size).replace('[capacity]', litres);
  });
}

function fillSelector(bundles, own) {
  const items = [...selector.querySelectorAll('[data-nudient-set-handle]')];
  const same = items.filter((item) => {
    const bundle = bundles[item.dataset.nudientSetHandle];
    return bundle && own.colour && bundle.colour === own.colour;
  });

  if (same.length < 2) {
    selector.setAttribute('hidden', '');
    return;
  }

  for (const item of items) {
    const bundle = bundles[item.dataset.nudientSetHandle];
    if (!same.includes(item)) {
      item.hidden = true;
      continue;
    }
    const pieces = bundle.parts.reduce((sum, part) => sum + part.quantity, 0);
    item.querySelector('[data-pieces]').textContent = (pieces === 1 ? selector.dataset.piecesOne : selector.dataset.piecesOther).replace('[count]', pieces);
    item.querySelector('[data-name]').textContent = bundle.parts.map((part) => part.size).join(' + ');
    item.hidden = false;
    item.removeAttribute('data-placeholder');
  }

  const ownPieces = own.parts.reduce((sum, part) => sum + part.quantity, 0);
  selector.querySelector('[data-summary]').textContent = (ownPieces === 1 ? selector.dataset.piecesOne : selector.dataset.piecesOther).replace('[count]', ownPieces);
}
